import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { FaPaperPlane } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { API_BASE_URL } from "../config";

/* -------------------- HELPERS -------------------- */
function sanitizeMarkdownLinks(text) {
  text = text.replace(/\[([^\]]+)\]/g, (_, label) => `[${label.replace(/\s+/g, " ").trim()}]`);
  text = text.replace(/\]\(\s*([^()]+)\s*\)/g, (_, urlPart) => {
    const trimmed = urlPart.trim();
    const lower = trimmed.toLowerCase();
    if (lower.startsWith("http") || lower.startsWith("mailto")) {
      const cleaned = trimmed
        .replace(/\s+/g, "")
        .replace(/\s*:\s*/g, ":")
        .replace(/\s*\/\s*/g, "/")
        .replace(/\s*\.\s*/g, ".");
      return `](${cleaned})`;
    }
    return `](${trimmed})`;
  });
  return text;
}

function fixDoubleStarSpacing(text) {
  if (typeof text !== "string") return text;

  console.log("DEBUG INPUT:", text);

  // Simple approach: Find all **...** patterns and trim internal whitespace
  // Pattern: ** followed by optional whitespace, content, optional whitespace, then **
  let cleaned = text.replace(/\*\*\s*([^*]+?)\s*\*\*/g, (match, content) => {
    const trimmed = content.trim();
    console.log("DEBUG MATCH:", match, "-> TRIMMED:", trimmed);
    return trimmed ? `**${trimmed}**` : "";
  });

  console.log("DEBUG OUTPUT:", cleaned);
  return cleaned;
}

const nowTime = () =>
  new Date().toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });


/* -------------------- COMPONENT -------------------- */
function UnifiedChat() {
  const { id } = useParams();

  const [chatHistory, setChatHistory] = useState([]);
  const [userMessage, setUserMessage] = useState("");
  const [chatbotDetails, setChatbotDetails] = useState({});
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef(null);
  const isHome = !id;

  /* ---------------- FETCH BOT ---------------- */
  useEffect(() => {
    if (!id) return;

    fetch(`${API_BASE_URL}/chatbot/${id}`)
      .then((res) => res.json())
      .then((data) => {
        setChatbotDetails(data);
        setChatHistory([]);
      })
      .catch(console.error);
  }, [id]);

  /* ---------------- AUTO SCROLL ---------------- */
  useLayoutEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, isTyping]);

  /* ---------------- SEND MESSAGE ---------------- */
  const sendMessage = useCallback(async () => {
    if (!userMessage.trim() || !id) return;

    const userMsg = {
      role: "user",
      content: userMessage,
      time: nowTime(),
    };

    setChatHistory((p) => [...p, userMsg]);
    setUserMessage("");
    setIsTyping(true);

    try {
      const res = await fetch(`${API_BASE_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chatbot_id: id,
          message: userMessage,
          chatHistory,
        }),
      });

      const data = await res.json();

      let botText = "Please try again later.";

      if (res.ok && data.response) {
        botText = Array.isArray(data.response.data)
          ? "Data received"
          : fixDoubleStarSpacing(sanitizeMarkdownLinks(data.response));
      }

      const botMsg = {
        role: "assistant",
        content: botText,
        time: nowTime(),
      };

      setChatHistory((p) => [...p, botMsg]);
    } catch {
      setChatHistory((p) => [
        ...p,
        { role: "assistant", content: "Please try again later.", time: nowTime() },
      ]);
    } finally {
      setIsTyping(false);
    }
  }, [userMessage, chatHistory, id]);
  return (
    <div className="flex flex-col h-full bg-slate-900 text-slate-100 overflow-hidden">

      {/* HEADER */}
      <header className="h-14 flex items-center px-6 border-b border-slate-800 bg-slate-950">
        <h2 className="text-sm font-semibold">
          {isHome ? "Welcome" : `Chat with ${chatbotDetails.chatbot_name}`}
        </h2>
      </header>

      {/* HOME */}
      {isHome ? (
        <div className="flex-1 flex items-center justify-center text-center">
          <h1 className="text-2xl font-bold text-pink-500">
            Apne Clone ko select Kare
          </h1>
        </div>
      ) : (
        <>
          {/* CHAT AREA */}
          <div className="flex-1 overflow-y-auto bg-gradient-to-b from-slate-900 to-slate-950">
            <div className="w-full md:max-w-3xl md:mx-auto px-4 py-4 space-y-4">

              {chatHistory.map((chat, i) => (
                <div
                  key={i}
                  className={`flex ${chat.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow
                  ${chat.role === "user"
                        ? "bg-emerald-500 text-slate-900 rounded-br-sm"
                        : "bg-slate-800 text-slate-100 rounded-bl-sm"}`}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeSanitize]}
                      components={{
                        strong: ({ node, ...props }) => (
                          <strong className="text-pink-500 font-bold" {...props} />
                        ),
                      }}
                    >
                      {chat.content}
                    </ReactMarkdown>

                    {/* TIME (12h format) */}
                    <div className="text-[10px] mt-1 opacity-60 text-right">
                      {chat.time}
                    </div>
                  </div>
                </div>
              ))}

              {/* TYPING DOTS */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="px-3 py-2 rounded-xl bg-slate-800 flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-150" />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-300" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* INPUT */}
          <footer className="border-t border-slate-800 bg-slate-950 px-4 py-3">
            <div className="max-w-3xl mx-auto flex gap-2 items-center">
              <input
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder={`Message ${chatbotDetails.chatbot_name}…`}
                className="flex-1 rounded-xl bg-slate-800 border border-slate-700
                         px-4 py-2.5 text-sm
                         focus:ring-2 focus:ring-emerald-500"
              />
              <button
                onClick={sendMessage}
                className="px-4 py-2.5 rounded-xl bg-emerald-500 text-slate-900"
              >
                <FaPaperPlane />
              </button>
            </div>
          </footer>
        </>
      )}
    </div>
  );


}

export default UnifiedChat;
